import { Injectable } from '@angular/core';

export interface Coordinates {
  x: number;
  y: number;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface TransformMatrix {
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
}

export interface LineData {
  id: string;
  naturalCoordinates: {
    y: number;
    leftX: number;
    rightX: number;
  };
  imageNaturalDimensions: {
    width: number;
    height: number;
  };
  metadata: {
    isSaved: boolean;
    dbId?: number;
    timestamp: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CoordinateTransformationService {
  private transformCache = new Map<string, TransformMatrix>();

  constructor() { }

  /**
   * Convert natural image coordinates to display coordinates
   */
  naturalToDisplay(naturalCoords: Coordinates, imageElement: HTMLImageElement): Coordinates {
    const matrix = this.getImageTransformMatrix(imageElement);
    return {
      x: matrix.offsetX + (naturalCoords.x * matrix.scaleX),
      y: matrix.offsetY + (naturalCoords.y * matrix.scaleY)
    };
  }

  /**
   * Convert display coordinates to natural image coordinates
   */
  displayToNatural(displayCoords: Coordinates, imageElement: HTMLImageElement): Coordinates {
    const matrix = this.getImageTransformMatrix(imageElement);
    return {
      x: Math.round((displayCoords.x - matrix.offsetX) / matrix.scaleX),
      y: Math.round((displayCoords.y - matrix.offsetY) / matrix.scaleY)
    };
  }

  /**
   * Convert mouse event to natural image coordinates
   */
  mouseEventToNaturalCoords(event: MouseEvent, imageElement: HTMLImageElement): Coordinates {
    const rect = imageElement.getBoundingClientRect();
    const displayCoords = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    return this.displayToNatural(displayCoords, imageElement);
  }

  /**
   * Get transformation matrix for converting between coordinate systems
   */
  getImageTransformMatrix(imageElement: HTMLImageElement): TransformMatrix {
    if (!imageElement || !imageElement.naturalWidth || !imageElement.naturalHeight) {
      return { scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 };
    }

    const cacheKey = this.getCacheKey(imageElement);
    if (this.transformCache.has(cacheKey)) {
      return this.transformCache.get(cacheKey)!;
    }

    const rect = imageElement.getBoundingClientRect();
    const container = imageElement.parentElement?.getBoundingClientRect();

    // Calculate scale factors
    const scaleX = rect.width / imageElement.naturalWidth;
    const scaleY = rect.height / imageElement.naturalHeight;

    // Calculate offsets (for centering in container)
    let offsetX = 0;
    let offsetY = 0;

    if (container) {
      offsetX = (container.width - rect.width) / 2;
      offsetY = (container.height - rect.height) / 2;
    }

    const matrix: TransformMatrix = {
      scaleX,
      scaleY,
      offsetX,
      offsetY
    };

    this.transformCache.set(cacheKey, matrix);
    return matrix;
  }

  /**
   * Clear transformation cache (call when image or container changes)
   */
  clearTransformCache(): void {
    this.transformCache.clear();
  }

  /**
   * Ensure coordinates are within image bounds
   */
  clampToImageBounds(coords: Coordinates, imageDimensions: ImageDimensions): Coordinates {
    return {
      x: Math.max(0, Math.min(coords.x, imageDimensions.width - 1)),
      y: Math.max(0, Math.min(coords.y, imageDimensions.height - 1))
    };
  }

  /**
   * Create standardized line data
   */
  createLineData(y: number, imageElement: HTMLImageElement, id?: string): LineData {
    return {
      id: id || `line-${Date.now()}`,
      naturalCoordinates: {
        y,
        leftX: 0,
        rightX: imageElement.naturalWidth - 1
      },
      imageNaturalDimensions: {
        width: imageElement.naturalWidth,
        height: imageElement.naturalHeight
      },
      metadata: {
        isSaved: false,
        timestamp: Date.now()
      }
    };
  }

  /**
   * Validate line data consistency
   */
  validateLineData(lineData: LineData, currentImageDimensions: ImageDimensions): boolean {
    return (
      lineData.imageNaturalDimensions.width === currentImageDimensions.width &&
      lineData.imageNaturalDimensions.height === currentImageDimensions.height
    );
  }

  private getCacheKey(imageElement: HTMLImageElement): string {
    const rect = imageElement.getBoundingClientRect();
    const container = imageElement.parentElement?.getBoundingClientRect();
    return `${imageElement.naturalWidth}x${imageElement.naturalHeight}_${rect.width}x${rect.height}_${container?.width || 0}x${container?.height || 0}`;
  }
} 